import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ViewerDataAdapter } from './contracts/adapter';
import type {
  AreaTag,
  LayoutHotspotSummary,
  LayoutMarker,
  PanoramaHotspot,
  PanoramaPhoto,
  ViewerEntityId,
  ViewerLayout,
  ViewerPhoto,
  ViewerRequestContext,
} from './contracts/models';
import { viewerQueryKeys } from './queryKeys';

const contextAdapterId = (
  adapter: ViewerDataAdapter,
  context: ViewerRequestContext,
): string => context.adapterId || adapter.id;

export const viewerQueryOptions = {
  layouts: (adapter: ViewerDataAdapter, context: ViewerRequestContext) => ({
    queryKey: viewerQueryKeys.layouts(
      contextAdapterId(adapter, context),
      context.projectId,
      context.layoutType,
    ),
    queryFn: () => adapter.queries.listLayouts({ context }),
  }),
  markers: (
    adapter: ViewerDataAdapter,
    context: ViewerRequestContext,
    layoutId: ViewerEntityId,
  ) => ({
    queryKey: viewerQueryKeys.markers(
      contextAdapterId(adapter, context),
      context.projectId,
      layoutId,
    ),
    queryFn: () => adapter.queries.getMarkers({ context, layoutId }),
  }),
  areaTags: (
    adapter: ViewerDataAdapter,
    context: ViewerRequestContext,
    layoutId: ViewerEntityId,
  ) => ({
    queryKey: viewerQueryKeys.areaTags(
      contextAdapterId(adapter, context),
      context.projectId,
      layoutId,
    ),
    queryFn: () => adapter.queries.getAreaTags({ context, layoutId }),
  }),
  availablePhotos: (
    adapter: ViewerDataAdapter,
    context: ViewerRequestContext,
    layoutId: ViewerEntityId,
  ) => ({
    queryKey: viewerQueryKeys.availablePhotos(
      contextAdapterId(adapter, context),
      context.projectId,
      layoutId,
    ),
    queryFn: () =>
      adapter.queries.getAvailablePhotos({ context, layoutId }),
  }),
  layoutHotspots: (
    adapter: ViewerDataAdapter,
    context: ViewerRequestContext,
    layoutId: ViewerEntityId,
  ) => ({
    queryKey: viewerQueryKeys.layoutHotspots(
      contextAdapterId(adapter, context),
      context.projectId,
      layoutId,
    ),
    queryFn: () =>
      adapter.queries.getLayoutHotspots({ context, layoutId }),
  }),
  panoramaPhoto: (
    adapter: ViewerDataAdapter,
    context: ViewerRequestContext,
    photoId: ViewerEntityId,
  ) => ({
    queryKey: viewerQueryKeys.panoramaPhoto(
      contextAdapterId(adapter, context),
      context.projectId,
      photoId,
    ),
    queryFn: () => adapter.queries.getPanoramaPhoto({ context, photoId }),
  }),
  photoHotspots: (
    adapter: ViewerDataAdapter,
    context: ViewerRequestContext,
    photoId: ViewerEntityId,
  ) => ({
    queryKey: viewerQueryKeys.photoHotspots(
      contextAdapterId(adapter, context),
      context.projectId,
      photoId,
    ),
    queryFn: () => adapter.queries.getPhotoHotspots({ context, photoId }),
  }),
};

export const useViewerLayouts = (
  adapter: ViewerDataAdapter,
  context: ViewerRequestContext,
  options: Omit<
    UseQueryOptions<readonly ViewerLayout[]>,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<readonly ViewerLayout[]> =>
  useQuery({
    ...viewerQueryOptions.layouts(adapter, context),
    ...options,
  });

export const useViewerMarkers = (
  adapter: ViewerDataAdapter,
  context: ViewerRequestContext,
  layoutId: ViewerEntityId,
  options: Omit<
    UseQueryOptions<readonly LayoutMarker[]>,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<readonly LayoutMarker[]> =>
  useQuery({
    ...viewerQueryOptions.markers(adapter, context, layoutId),
    ...options,
  });

export const useViewerAreaTags = (
  adapter: ViewerDataAdapter,
  context: ViewerRequestContext,
  layoutId: ViewerEntityId,
  options: Omit<
    UseQueryOptions<readonly AreaTag[]>,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<readonly AreaTag[]> =>
  useQuery({
    ...viewerQueryOptions.areaTags(adapter, context, layoutId),
    ...options,
  });

export const useViewerAvailablePhotos = (
  adapter: ViewerDataAdapter,
  context: ViewerRequestContext,
  layoutId: ViewerEntityId,
  options: Omit<
    UseQueryOptions<readonly ViewerPhoto[]>,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<readonly ViewerPhoto[]> =>
  useQuery({
    ...viewerQueryOptions.availablePhotos(adapter, context, layoutId),
    ...options,
  });

export const useViewerLayoutHotspots = (
  adapter: ViewerDataAdapter,
  context: ViewerRequestContext,
  layoutId: ViewerEntityId,
  options: Omit<
    UseQueryOptions<readonly LayoutHotspotSummary[]>,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<readonly LayoutHotspotSummary[]> =>
  useQuery({
    ...viewerQueryOptions.layoutHotspots(adapter, context, layoutId),
    ...options,
  });

export const useViewerPanoramaPhoto = (
  adapter: ViewerDataAdapter,
  context: ViewerRequestContext,
  photoId: ViewerEntityId,
  options: Omit<
    UseQueryOptions<PanoramaPhoto>,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<PanoramaPhoto> =>
  useQuery({
    ...viewerQueryOptions.panoramaPhoto(adapter, context, photoId),
    ...options,
  });

export const useViewerPhotoHotspots = (
  adapter: ViewerDataAdapter,
  context: ViewerRequestContext,
  photoId: ViewerEntityId,
  options: Omit<
    UseQueryOptions<readonly PanoramaHotspot[]>,
    'queryKey' | 'queryFn'
  > = {},
): UseQueryResult<readonly PanoramaHotspot[]> =>
  useQuery({
    ...viewerQueryOptions.photoHotspots(adapter, context, photoId),
    ...options,
  });
